import React from 'react'
import PropTypes from 'prop-types'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { usePanelPartDrag } from 'hooks/dash'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import strings from 'utils/strings'
import {
  REMOVE,
  COMPONENT,
  ADD,
  VEHICLE_TYPE_CHEC_PLUS,
  DASH_VIEW,
  TRUCK_SUBTYPE_38N,
} from 'utils/constants'
import {
  getComponent,
  getShouldLocationShowAddBtn,
  getHasComponentMoved,
  getDashVehicleSerialNumber,
  getDashVehicleType,
  getVehicleSubType,
} from 'selectors/dash'
import { getCanEditSwitchAndGauge, hasRights } from 'selectors/authorization'
import {
  EDIT_DASHBOARD_LAYOUT,
  EDIT_PLUS_DASHBOARD_LAYOUT,
} from 'constants/authorization'
import { getTruckIsPreProduction } from 'selectors/truck'
import { Icon, Button, Paragraph } from 'components/patterns'
import {
  isEmptyPanelLocation,
  isUnassignedComponent,
  getComponentName,
  getComponentDescription,
} from 'components/SwitchAndGauge/utils'

import {
  SMART_SWITCH,
  PLUG,
  HARDWIRED_SWITCH,
} from 'components/SwitchAndGauge/definitions/dashConstants'
import { setSelectedIdWithDrawerAndMeta } from 'actions/navigator'
import {
  PartDescription,
  RemovePart,
  StyledDragItem,
  StyledLocationDetails,
  StyledParagraphSmall,
  LocationDetailsSection,
  StyledColumnDiv,
  StyledPartNumberContainer,
  CapDetailsSection,
} from './DraggablePart.style'

const getCanRemove = (
  switchId,
  type,
  isPreProduction,
  vehicleType,
  vehicleSubType,
  canEdit,
  isPlug = false,
  panelNumber = ''
) =>
  (panelNumber.includes('TBBC') && !isPreProduction) ||
  (canEdit &&
    vehicleType === VEHICLE_TYPE_CHEC_PLUS &&
    (switchId || isPlug) &&
    (type === SMART_SWITCH ||
      type === HARDWIRED_SWITCH ||
      (isPlug && type === PLUG.toLowerCase()) ||
      vehicleSubType === TRUCK_SUBTYPE_38N) &&
    !isPreProduction)

const DraggablePart = ({
  location,
  viewDetails,
  children,
  displayLocation,
  className,
}) => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const isDashBuilderView = pathname.includes('/dash-layout/')

  const component = useSelector(getComponent, location)
  const {
    panelNumber,
    type,
    switchId,
    bom,
    capPartNumber,
    capDescription,
    isPlug,
  } = component
  const serialNumber = useSelector(getDashVehicleSerialNumber)
  const vehicleType = useSelector(getDashVehicleType)
  const vehicleSubType = useSelector(getVehicleSubType)
  const isPreProduction = useSelector(getTruckIsPreProduction, { serialNumber })
  const hasMoved = useMakeSelector(getHasComponentMoved, location)
  const canEditSG = useSelector(getCanEditSwitchAndGauge, {
    serialNumber,
  })
  const canEditDashLayout = useSelector(hasRights, {
    rights: [EDIT_DASHBOARD_LAYOUT, EDIT_PLUS_DASHBOARD_LAYOUT],
  })
  const canEdit = canEditSG || (isDashBuilderView && canEditDashLayout)
  const { isDraggable, dragRef } = usePanelPartDrag({
    location,
    serialNumber,
  })

  const shouldShowAdd = useSelector(getShouldLocationShowAddBtn, location)
  const showAdd = canEdit && shouldShowAdd && !isPreProduction

  const description =
    getComponentDescription(component) ||
    getComponentName(component) ||
    strings.empty
  const isEmptyLocation = panelNumber
    ? false
    : isEmptyPanelLocation(description)

  const isUnassigned = isUnassignedComponent(location)

  const canRemove = getCanRemove(
    switchId,
    type,
    isPreProduction,
    vehicleType,
    vehicleSubType,
    canEdit,
    isPlug,
    panelNumber
  )

  const showEmpty = isEmptyLocation && !showAdd && !isUnassigned
  const showListItem = !isEmptyLocation && !showAdd

  const handleOnClick = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: COMPONENT,
        id: location,
      })
    )
  }

  const handleRemoveSwitch = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: REMOVE,
        id: location,
      })
    )
  }

  const handleAddSwitch = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: ADD,
        id: location,
      })
    )
  }

  if (children) {
    return (
      <StyledDragItem
        ref={canEdit ? dragRef : null}
        hasMoved={hasMoved}
        isDraggable={isDraggable}
        className={className}
        data-testid="drag-item"
      >
        {children}
      </StyledDragItem>
    )
  }

  return (
    <>
      <StyledDragItem
        ref={canEdit ? dragRef : null}
        hasMoved={hasMoved}
        isDraggable={isDraggable}
        className={className}
        data-testid="drag-item"
      >
        {showAdd && (
          <>
            <StyledLocationDetails>{location}</StyledLocationDetails>
            <Button onClick={handleAddSwitch} link>
              {strings.add_component}
            </Button>
          </>
        )}
        {showEmpty && (
          <>
            <StyledLocationDetails>{location}</StyledLocationDetails>
            <span>{description}</span>
          </>
        )}
        {showListItem && (
          <>
            {!isUnassigned && (
              <StyledLocationDetails>
                {displayLocation || location}
              </StyledLocationDetails>
            )}
            <PartDescription title={panelNumber}>
              <Button link onClick={handleOnClick}>
                {description}
              </Button>
            </PartDescription>
            {canRemove && (
              <RemovePart title={strings.remove_switch} placement="left">
                <Icon
                  onClick={handleRemoveSwitch}
                  icon="plus-circle"
                  style={{
                    color: '#4f5764',
                    fontSize: '20px',
                    transform: 'rotate(45deg)',
                  }}
                  fill="outlined"
                  hoverFill="outlined"
                  hoverColor="#94040a"
                />
              </RemovePart>
            )}
          </>
        )}
      </StyledDragItem>
      {!isUnassigned && viewDetails && (
        <LocationDetailsSection isUnassigned={isUnassigned}>
          <StyledPartNumberContainer>
            <StyledParagraphSmall size="small" weight="bold">
              {strings.part_number}
            </StyledParagraphSmall>
            <Paragraph>{panelNumber}</Paragraph>
          </StyledPartNumberContainer>
          {bom && (
            <StyledColumnDiv>
              <StyledParagraphSmall size="small" weight="bold">
                {strings.bom_title}
              </StyledParagraphSmall>
              <Paragraph>{bom}</Paragraph>
            </StyledColumnDiv>
          )}
        </LocationDetailsSection>
      )}
      {!isUnassigned && viewDetails && capDescription && capPartNumber && (
        <CapDetailsSection isUnassigned={isUnassigned}>
          <StyledPartNumberContainer>
            <StyledParagraphSmall size="small" weight="bold">
              {strings.cap_part_no}
            </StyledParagraphSmall>
            <Paragraph>{capPartNumber}</Paragraph>
          </StyledPartNumberContainer>
          <StyledColumnDiv>
            <StyledParagraphSmall size="small" weight="bold">
              {strings.cap_description}
            </StyledParagraphSmall>
            <Paragraph>{capDescription}</Paragraph>
          </StyledColumnDiv>
        </CapDetailsSection>
      )}
    </>
  )
}

DraggablePart.propTypes = {
  location: PropTypes.string,
  children: PropTypes.node,
  viewDetails: PropTypes.bool,
  displayLocation: PropTypes.string,
  className: PropTypes.string,
}

export default DraggablePart
